import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Hook che gestisce una connessione WebSocket persistente verso /api/ws per
 * ricevere messaggi, typing, read receipt e presenza in tempo reale.
 *
 * - Riconnette automaticamente con backoff esponenziale (max 30s)
 * - Ping ogni 25s per tenere viva la connessione dietro proxy/Cloudflare
 * - Chiude e riapre al cambio del token (login/logout)
 *
 * Uso:
 *   const { connected, send, lastEvent } = useRealtimeMessages({ onEvent: (e) => {...} });
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const WS_PROTOCOL = BACKEND.startsWith('https') ? 'wss' : 'ws';
const WS_HOST = BACKEND.replace(/^https?:\/\//, '');
const WS_URL = `${WS_PROTOCOL}://${WS_HOST}/api/ws`;
const PING_INTERVAL_MS = 25_000;
const MAX_BACKOFF_MS = 30_000;

export function useRealtimeMessages({ onEvent } = {}) {
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const pingTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const backoffRef = useRef(1000);
  const stoppedRef = useRef(false);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const getToken = () => {
    try { return localStorage.getItem('agn_token'); } catch { return null; }
  };

  const connect = useCallback(() => {
    const token = getToken();
    if (!token) return; // guest — nessuna WS
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN ||
                           wsRef.current.readyState === WebSocket.CONNECTING)) return;
    try {
      const ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        backoffRef.current = 1000; // reset backoff after successful connect
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = setInterval(() => {
          try { ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ type: 'ping' })); } catch {}
        }, PING_INTERVAL_MS);
      };
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          setLastEvent(data);
          onEventRef.current?.(data);
        } catch {}
      };
      ws.onerror = () => { /* onclose will fire next */ };
      ws.onclose = () => {
        setConnected(false);
        clearInterval(pingTimerRef.current);
        if (!stoppedRef.current && getToken()) {
          clearTimeout(reconnectTimerRef.current);
          const delay = Math.min(backoffRef.current, MAX_BACKOFF_MS);
          reconnectTimerRef.current = setTimeout(connect, delay);
          backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS);
        }
      };
    } catch {
      // fallback: schedule retry
      reconnectTimerRef.current = setTimeout(connect, 3000);
    }
  }, []);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try { ws.send(JSON.stringify(payload)); return true; } catch { return false; }
  }, []);

  useEffect(() => {
    stoppedRef.current = false;
    connect();
    return () => {
      stoppedRef.current = true;
      clearInterval(pingTimerRef.current);
      clearTimeout(reconnectTimerRef.current);
      try { wsRef.current?.close(); } catch {}
    };
  }, [connect]);

  return { connected, send, lastEvent };
}

export default useRealtimeMessages;
